import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

// Open modals, so Escape only closes the innermost one (e.g. a fullscreen viewer inside a dialog).
// Nesting depth comes from context because nested effects run child-first.
const openModals: { id: symbol; depth: number }[] = [];
const ModalDepth = createContext(0);
let bodyOverflowBeforeModals = '';

const isTopmost = (id: symbol) =>
  openModals.reduce((top, entry) => (entry.depth >= top.depth ? entry : top), openModals[0]!).id === id;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** id of the element that titles the dialog. */
  labelledBy?: string;
  variant?: 'dialog' | 'fullscreen';
  /** Classes for the dialog panel. */
  className?: string;
  closeOnOverlayClick?: boolean;
  children: ReactNode;
}

export function Modal({
  open,
  onClose,
  labelledBy,
  variant = 'dialog',
  className,
  closeOnOverlayClick = variant === 'dialog',
  children,
}: ModalProps) {
  const depth = useContext(ModalDepth) + 1;
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const entry = { id: Symbol('modal'), depth };
    if (openModals.length === 0) {
      bodyOverflowBeforeModals = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    openModals.push(entry);
    const previousFocus = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isTopmost(entry.id)) onCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      openModals.splice(openModals.indexOf(entry), 1);
      if (openModals.length === 0) document.body.style.overflow = bodyOverflowBeforeModals;
      previousFocus?.focus?.();
    };
  }, [open, depth]);

  if (!open) return null;

  return createPortal(
    <div
      className={
        variant === 'dialog'
          ? 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm overflow-y-auto'
          : 'fixed inset-0 z-50 flex flex-col p-4 sm:p-6 bg-slate-950/95 backdrop-blur-2xl text-slate-100'
      }
      onMouseDown={(event) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={cn('outline-none', variant === 'fullscreen' && 'flex flex-col flex-1 min-h-0', className)}
      >
        <ModalDepth.Provider value={depth}>{children}</ModalDepth.Provider>
      </div>
    </div>,
    document.body,
  );
}
